import { useState } from "react";
import type { GuardrailNote } from "../api/types";
import { AlertIcon, ChevronDownIcon } from "./Icon";

const SOURCE_LABEL: Record<GuardrailNote["source"], string> = {
  pattern: "Pattern match",
  density: "Density check",
  screen: "Model screen",
  external: "External check",
};

export default function GuardrailNotes({ notes }: { notes: GuardrailNote[] }) {
  const [open, setOpen] = useState(false);
  if (notes.length === 0) return null;

  const hasWarnings = notes.some((note) => note.severity !== "info");
  const containerClass = hasWarnings ? "border-warn-border bg-warn-surface" : "border-border bg-surface";
  const headerTextClass = hasWarnings ? "text-warn" : "text-muted";

  return (
    <div className={`rounded-lg border ${containerClass}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
        aria-expanded={open}
      >
        <span className={`flex items-center gap-2 text-sm font-medium ${headerTextClass}`}>
          <AlertIcon width={16} height={16} />
          Guardrail findings · {notes.length} {notes.length === 1 ? "note" : "notes"}
        </span>
        <ChevronDownIcon
          width={16}
          height={16}
          className={`shrink-0 transition-transform ${headerTextClass} ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open ? (
        <ul className={`space-y-4 border-t px-4 py-3 ${hasWarnings ? "border-warn-border" : "border-border"}`}>
          {notes.map((note, i) => (
            <GuardrailNoteRow key={i} note={note} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function GuardrailNoteRow({ note }: { note: GuardrailNote }) {
  const quiet = note.severity === "info";

  return (
    <li className="text-sm">
      <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xs">
        <span
          className={
            "rounded px-1.5 py-0.5 font-medium " + (quiet ? "bg-bg text-muted" : "bg-bg text-warn")
          }
        >
          {note.category} · {note.severity}
        </span>
        <span className="rounded bg-bg px-1.5 py-0.5 text-muted">{SOURCE_LABEL[note.source]}</span>
      </div>
      {note.source_quote ? (
        <blockquote
          className={
            "border-l-2 pl-3 italic text-muted " + (quiet ? "border-border" : "border-warn-border")
          }
        >
          “{note.source_quote}”
        </blockquote>
      ) : null}
      <p className={"mt-1.5 " + (quiet ? "text-muted" : "text-text")}>{note.detail}</p>
      {quiet ? <p className="mt-1 text-xs text-muted">Screening judged the flagged content benign.</p> : null}
    </li>
  );
}
